import type {
  JournalCoverage,
  JournalFacts,
  JournalSummaryResponse,
  JournalSummaryUnavailable,
  JournalUsual,
  RecordingRecord,
} from '../../lib/api'
import { formatPeriodName, formatSessionDate } from '../../lib/format'
import { NUMERIC_FIELDS, hasAnswer } from '../../lib/selfReport'
import type { SummaryFact } from '../GroupHeader'

/**
 * The Journal view's read model over `GET /users/{id}/journal-summary`
 * (Sprint 1.9.11 J2c, concept §B2/§B3).
 *
 * ── THIS MODULE PRODUCES NO NUMBER OF ITS OWN ───────────────────────────────
 *
 * Every figure the grouped Journal view shows — the six facts, the coverage
 * counts, the day totals behind "Show the 6 days", the group labels and the
 * "Too few journals for a summary" state — arrives in the payload already
 * computed and already rounded. `brainspot/contracts/journal_summary.py` says
 * why in terms: *the producer rounds; the client formats*, and
 * `woke_at_mean` travels as `"HH:MM"` precisely because turning minutes into a
 * clock reading would be the one piece of arithmetic the rule exists to forbid.
 * So there is not one `+`, `-`, `*`, `/` or `Math.*` below, and
 * `src/test/clientScience.test.ts` holds this file (and this folder) to that
 * floor rather than taking the comment's word for it.
 *
 * **The one number the page owns is how much of a period is ON SCREEN.**
 * `loadedDays` counts the distinct `session_date`s the paged list has actually
 * read, which is a fact about this page and not about the journal; its
 * denominator is the payload's `coverage.days`, which is in the contract for
 * exactly this reason ("without it the web would count the rows it had LOADED
 * and under-report on a part-loaded page").
 *
 * ── A NULL FACT IS LEFT OUT, NEVER ZERO-FILLED ──────────────────────────────
 *
 * No fact is zero-filled: "nobody answered the sleep question this week" and
 * "everybody slept zero hours" are different facts and the payload keeps them
 * apart, so `hasAnswer` is the only test applied and a `0` that was measured
 * survives as `0`. An absent fact is omitted from the line rather than rendered
 * as a dash: the line is a list of what IS known, and six dashes beside one
 * value reads as five measured absences.
 *
 * The two count pairs travel together or not at all. `exercised_days` without
 * `answered_days` cannot be said — "exercised on 7 days" means nothing without
 * how many days were asked, and dividing them to say so is what the contract
 * refuses to make the client do.
 */

/** The six facts, in the one order the design shows them (concept §B2). */
export function summaryFacts(facts: JournalFacts | null | undefined): SummaryFact[] {
  if (facts === null || facts === undefined) return []
  const out: SummaryFact[] = []
  if (hasAnswer(facts.sleep_hours_mean)) {
    // The FORM's unit, not a second vocabulary: the table reads a night back as
    // "7.5 hours" and the week's mean has to read the same way.
    out.push({ icon: 'moon', text: `${facts.sleep_hours_mean} ${NUMERIC_FIELDS.sleep_hours.unit}` })
  }
  // Already "HH:MM" — printed, never derived.
  if (hasAnswer(facts.woke_at_mean) && facts.woke_at_mean !== '') {
    out.push({ icon: 'sun', text: `woke ${facts.woke_at_mean}` })
  }
  if (hasAnswer(facts.stress_median)) {
    out.push({ icon: 'activity', text: `stress ${facts.stress_median}` })
  }
  if (hasAnswer(facts.caffeine_cups_per_day_mean)) {
    out.push({
      icon: 'coffee',
      text: `${facts.caffeine_cups_per_day_mean} ${NUMERIC_FIELDS.caffeine_cups.unit} a day`,
    })
  }
  if (hasAnswer(facts.exercised_days) && hasAnswer(facts.answered_days)) {
    out.push({
      icon: 'dumbbell',
      text: `exercised ${facts.exercised_days} of ${facts.answered_days} days`,
      // Dimmed because the ANSWER was none, which is the only state an icon on
      // this screen carries. Never a tone, never a judgement of the number.
      none: facts.exercised_days === 0,
    })
  }
  if (hasAnswer(facts.alcohol_evenings) && hasAnswer(facts.answered_evenings)) {
    out.push({
      icon: 'wine',
      text: `alcohol ${facts.alcohol_evenings} of ${facts.answered_evenings} evenings`,
      none: facts.alcohol_evenings === 0,
    })
  }
  return out
}

/**
 * ── WHY A GROUP HAS NO FACTS, IN THE WORDS THAT ARE TRUE OF IT ──────────────
 *
 * The payload says why, and for a while this file did not ask. The view
 * inferred "no summary" from `summaryFacts` coming back empty and showed
 * `GroupHeader`'s default sentence — "Too few journals for a summary" — for
 * every empty line. That sentence is a MEASUREMENT, and it was wrong for the
 * one case it was most likely to meet: a week with five answered journals none
 * of which touched sleep, stress, caffeine, exercise or alcohol has plenty of
 * journals and still no facts, and telling that reader there were too few is
 * telling them something false about their own week.
 *
 * So the reason is read from the payload. One code exists today and it is the
 * only one given a sentence: `brainspot/contracts/journal_summary.py` types
 * `JournalSummaryUnavailable` as `Literal["too_few_journals"]`, and inventing
 * words for a code the server cannot send would be inventing the state too.
 * The map is keyed by the generated union, so the day the backend adds a code
 * the build stops here rather than quietly falling back.
 */
const UNAVAILABLE_TEXT: Record<JournalSummaryUnavailable, string> = {
  too_few_journals: 'Too few journals for a summary',
}

/**
 * Enough journals, and no fact among them. NOT the too-few case and not an
 * error: the questions were simply left blank, which is a thing a person is
 * allowed to do and the one sentence that says so.
 */
const NOTHING_ANSWERED = 'These journals answer none of the six questions'

/**
 * The payload sent neither facts nor a reason, which
 * `brainspot/contracts/journal_summary.py` says cannot happen ("`None` exactly
 * when `unavailable_reason` is set, and never both"). A newer server that
 * breaks that invariant, or an older one this build has not seen, gets the one
 * sentence that claims nothing about why.
 */
const NO_SUMMARY = 'No summary for this period'

/**
 * What to say in place of the facts, or `undefined` when there are facts to
 * show and nothing to say.
 *
 * Takes the group (or `usual`) rather than the reason alone, because the
 * answer depends on BOTH halves: a reason code decides it outright, and
 * without one the question becomes whether `facts` arrived with anything in it.
 */
export function unavailableText(summary: {
  facts?: JournalFacts | null
  unavailable_reason?: JournalSummaryUnavailable | null
}): string | undefined {
  const reason = summary.unavailable_reason
  // `== null` on purpose, as the rest of this folder reads an absent key: a
  // payload from a server that predates the field carries neither `null` nor
  // the key, and the two absences mean the same thing here. `hasAnswer` cannot
  // be used, because its `unknown` argument does not narrow the index below.
  if (reason != null) return UNAVAILABLE_TEXT[reason] ?? NO_SUMMARY
  if (summaryFacts(summary.facts).length > 0) return undefined
  return summary.facts == null ? NO_SUMMARY : NOTHING_ANSWERED
}

/** `n` with the noun it takes. An equality test, not a measurement. */
const counted = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`

/** "4 journals of 5 recordings · recorded 08:31–09:12" (concept §B2). */
export function groupCoverageText(coverage: JournalCoverage): string {
  const parts = [
    `${counted(coverage.journals, 'journal', 'journals')} of ${counted(coverage.recordings, 'recording', 'recordings')}`,
  ]
  // Absent for a period whose recordings all predate Sprint 1.9.9, which is
  // when an acquisition instant started travelling with the row.
  if (hasAnswer(coverage.recorded_time_range) && coverage.recorded_time_range !== '') {
    parts.push(`recorded ${coverage.recorded_time_range}`)
  }
  return parts.join(' · ')
}

/** "16 journals · 14 Sep 2026 – 02 Oct 2026 · recorded 08:31–10:20, most often 08:50". */
export function usualCoverageText(usual: JournalUsual): string {
  const parts = [counted(usual.coverage.journals, 'journal', 'journals')]
  // The app's own `session_date` formatter, so the range reads in the same
  // words as the dates in the column under it.
  if (hasAnswer(usual.start_date) && hasAnswer(usual.end_date)) {
    parts.push(`${formatSessionDate(usual.start_date)} – ${formatSessionDate(usual.end_date)}`)
  }
  const range = usual.coverage.recorded_time_range
  if (hasAnswer(range) && range !== '') {
    const mode = usual.recorded_time_mode
    parts.push(
      hasAnswer(mode) && mode !== ''
        ? `recorded ${range}, most often ${mode}`
        : `recorded ${range}`,
    )
  }
  return parts.join(' · ')
}

/**
 * One period, ready to become a `RowGroup`.
 *
 * Data and not JSX, because this file is a `.ts` beside the cells rather than a
 * component: `History.tsx` turns each of these into a `GroupHeader` and a
 * footer. The split also makes the whole grouping decision testable without
 * rendering a table.
 */
export interface JournalPeriod {
  key: string
  /** The payload's own label: "Week of 28 Sep", "October 2026". */
  label: string
  /** The mono coverage line, or `''` when there is nothing to say. */
  coverage: string
  /** Empty when the payload reports no summary — the header then says so. */
  facts: SummaryFact[]
  /**
   * The muted sentence to show in place of the facts. Set whenever `facts` is
   * empty, from the payload's own `unavailable_reason` — the header's default
   * sentence is a fallback for a caller that knows no reason, never the answer
   * for a group whose reason arrived in the response.
   */
  unavailableText?: string
  /** The loaded rows of this period, in the order the list read them. */
  rows: RecordingRecord[]
  /** Hidden behind "Show the N days" (concept §B3). */
  collapsed: boolean
  /** Whether this period has a disclosure at all: the open ones do not. */
  collapsible: boolean
  /** The period's own day count, from the payload. */
  days: number
  /** How many of those days this page has read. */
  loadedDays: number
  /** "21 Sep" / "October", for the disclosure's words. */
  periodName: string
}

/**
 * How many periods start OPEN, newest first.
 *
 * Two weeks and one month, which is the concept: by week "groups older than the
 * two most recent start collapsed", by month "only the current month is open".
 *
 * The newest group IS the current period, because `journal-summary` sends them
 * newest first and marks the running one " · so far" itself. Asking a clock
 * what month it is would put a second answer to that question in the client,
 * and the two would disagree for anyone whose zone differs from the one the
 * payload was built in.
 */
const OPEN_PERIODS: Record<'week' | 'month', number> = { week: 2, month: 1 }

/** Title and reason for loaded days no returned period covers. */
const UNSUMMARISED_LABEL = 'Other days'
const UNSUMMARISED_REASON = 'These days are not in the summary yet'

/**
 * The payload's periods, each carrying the loaded rows that fall inside it.
 *
 * ── NO ROW IS EVER DROPPED ──────────────────────────────────────────────────
 *
 * `groups` is paginated (`next_cursor`) and the recordings list pages
 * independently, so the two can disagree about how far back they reach. Any
 * loaded row that no returned period covers goes into one last period that says
 * exactly that, rather than vanishing from a view whose whole job is to show a
 * person every answer they wrote.
 *
 * `expanded` is the keys a reader has opened by hand. A period is open if it is
 * one of the newest `OPEN_PERIODS` or the reader opened it — a `Set`, and not a
 * comparison against an index, so there is no `>` here to explain.
 */
export function journalPeriods(
  rows: readonly RecordingRecord[],
  summary: JournalSummaryResponse,
  { expanded }: { expanded: ReadonlySet<string> } = { expanded: new Set() },
): JournalPeriod[] {
  const defaultOpen = new Set(
    summary.groups.slice(0, OPEN_PERIODS[summary.group]).map((group) => group.key),
  )
  const open = new Set<string>([...defaultOpen, ...expanded])
  const placed = new Set<string>()
  const periods = summary.groups.map((group) => {
    // Inclusive local-date bounds, compared as the ISO strings they are: the
    // contract sends them so "the web never computes a week boundary".
    const mine = rows.filter(
      (row) => row.session_date >= group.start_date && row.session_date <= group.end_date,
    )
    for (const row of mine) placed.add(row.recording_id)
    return {
      key: group.key,
      label: group.label,
      coverage: groupCoverageText(group.coverage),
      facts: summaryFacts(group.facts),
      // The payload's own reason, not an inference from the line being empty.
      unavailableText: unavailableText(group),
      rows: mine,
      collapsed: !open.has(group.key),
      collapsible: !defaultOpen.has(group.key),
      days: group.coverage.days,
      loadedDays: new Set(mine.map((row) => row.session_date)).size,
      periodName: formatPeriodName(group.start_date, summary.group),
    } satisfies JournalPeriod
  })
  const orphans = rows.filter((row) => !placed.has(row.recording_id))
  if (orphans.length === 0) return periods
  return [
    ...periods,
    {
      key: 'unsummarised',
      label: UNSUMMARISED_LABEL,
      coverage: '',
      facts: [],
      unavailableText: UNSUMMARISED_REASON,
      rows: orphans,
      collapsed: false,
      collapsible: false,
      days: new Set(orphans.map((row) => row.session_date)).size,
      loadedDays: new Set(orphans.map((row) => row.session_date)).size,
      periodName: UNSUMMARISED_LABEL,
    },
  ]
}
